import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Description as FileIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

export default function AttachmentList({ eventId, canEdit }) {
  const [attachments, setAttachments] = useState([]);
  const { user } = useAuth();

  const fetchAttachments = useCallback(async () => {
    const { data, error } = await supabase
      .from('event_attachments')
      .select(`
        id, 
        file_name,
        file_path,
        file_type,
        file_size,
        uploaded_at,
        uploaded_by
      `)
      .eq('event_id', eventId)
      .order('uploaded_at', { ascending: false });

    if (error) {
      console.error('Erreur lors de la récupération des pièces jointes:', error);
      return;
    }

    // Récupérer les full_name des utilisateurs liés
    const userIds = [...new Set(data.map(a => a.uploaded_by).filter(Boolean))];
    let usersMap = {};
    if (userIds.length > 0) {
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, full_name')
        .in('id', userIds);
      if (!usersError && usersData) {
        usersMap = Object.fromEntries(usersData.map(u => [u.id, u.full_name]));
      }
    }

    // Associer le nom complet à chaque pièce jointe
    const attachmentsWithNames = data.map(a => ({
      ...a,
      full_name: usersMap[a.uploaded_by] || a.uploaded_by
    }));

    setAttachments(attachmentsWithNames);
  }, [eventId]);

  useEffect(() => {
    if (eventId) fetchAttachments();
  }, [eventId, fetchAttachments]);

  if (!eventId) return null;

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const filePath = `${eventId}/${file.name}`;

      const { error: uploadError } = await supabase.storage
        .from('event-attachments')
        .upload(filePath, file, {
          cacheControl: '3600'
        });

      if (uploadError) {
        if (uploadError.statusCode === '413') {
          alert("Le fichier est trop volumineux. Taille limite atteinte.");
        } else if (uploadError.statusCode === '415') {
          alert("Le type de fichier n'est pas autorisé par le serveur.");
        } else {
          alert(`Erreur lors de l'upload: ${uploadError.message || uploadError.error}`);
        }
        console.error('Erreur lors de l\'upload:', uploadError);
        return;
      }

      const { error: dbError } = await supabase
        .from('event_attachments')
        .insert({
          event_id: eventId,
          file_name: file.name,
          file_path: filePath,
          file_type: file.type,
          file_size: file.size,
          uploaded_by: user.id,
          owner: user.id
        });

      if (dbError) {
        console.error('Erreur lors de l\'enregistrement:', dbError);
        return;
      }

      fetchAttachments();
    } catch (error) {
      console.error('Erreur inattendue:', error);
    }
  };

  const handleDownload = async (attachment) => {
    const { data, error } = await supabase.storage
      .from('event-attachments')
      .download(attachment.file_path);

    if (error) {
      console.error('Erreur lors du téléchargement:', error);
      return;
    }

    // Créer un blob à partir des données
    const blob = new Blob([data], { type: attachment.file_type || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = attachment.file_name;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleDelete = async (attachment) => {
    // Vérifier d'abord si le fichier existe
    const { data: checkData, error: checkError } = await supabase.storage
      .from('event-attachments')
      .list(attachment.file_path.split('/')[0]); // Vérifie le dossier de l'événement

    if (checkError) {
      console.error('Erreur lors de la vérification du fichier:', checkError);
      return;
    }

    // Si le fichier existe, le supprimer
    if (checkData.some(file => file.name === attachment.file_path.split('/').pop())) {
      const { error: storageError } = await supabase.storage
        .from('event-attachments')
        .remove([attachment.file_path]);

      if (storageError) {
        console.error('Erreur lors de la suppression du fichier:', storageError);
        return;
      }
    }

    // Supprimer l'enregistrement de la base
    const { error: dbError } = await supabase
      .from('event_attachments')
      .delete()
      .eq('id', attachment.id);

    if (dbError) {
      console.error('Erreur lors de la suppression de l\'enregistrement:', dbError);
      return;
    }

    fetchAttachments();
  };

  return (
    <Box>
      {canEdit && (
        <Button
          variant="outlined"
          component="label"
          startIcon={<UploadIcon />}
        >
          Ajouter un fichier
          <input
            type="file"
            hidden
            onChange={handleFileUpload}
          />
        </Button>
      )}

      <List>
        {attachments.map((attachment) => (
          <ListItem key={attachment.id}>
            <ListItemIcon>
              <FileIcon />
            </ListItemIcon>
            <ListItemText
              primary={attachment.file_name}
              secondary={`Ajouté par ${attachment.full_name}`}
            />
            <ListItemSecondaryAction>
              <IconButton onClick={() => handleDownload(attachment)}>
                <DownloadIcon />
              </IconButton>
              {canEdit && attachment.uploaded_by === user.id && (
                <IconButton onClick={() => handleDelete(attachment)}>
                  <DeleteIcon />
                </IconButton>
              )}
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
